/* eslint-disable @typescript-eslint/no-explicit-any */

// 1. Add this module augmentation at the top of the file (or in a global .d.ts)
declare module '@mui/x-data-grid' {
  interface FooterPropsOverrides {
    footer?: React.ReactNode;
    summary?: any;
    rows: any[];
    columns: any[];
    pagination?: boolean;
  }
  interface ToolbarPropsOverrides {
    canExport?: any;
    canPrint?: any;
    search?: any;
    headerProps?: any;
    isHeader?: boolean;
    printOptions?: any;
    onPrint?: () => void;
    onExportCsv?: () => void; // ← add
  }
  interface NoRowsOverlayPropsOverrides {
    message?: string;
  }
}

import {
  DataGrid,
  gridFilteredSortedRowIdsSelector,
  GridLoadingOverlay,
  gridPaginatedVisibleSortedGridRowEntriesSelector,
  GridPagination,
  GridSortModel,
  useGridApiRef,
} from '@mui/x-data-grid';

import React, { useCallback, useMemo, useState } from 'react';

import CustomGridToolbar from './CustomGridToolbar';
import CustomSummary from './DataGridSummary';

import { Theme } from '@mui/material/styles';
import { SxProps } from '@mui/material';
import CustomDataGridNoRowOverlay from './CustomDataGridNoRowOverlay';

// ─────────────────────────────────────────────────────────────────────────────
// Static constants — defined OUTSIDE the component so they are never recreated
// ─────────────────────────────────────────────────────────────────────────────

const BASE_SX: SxProps<Theme> = {
  '& .MuiDataGrid-columnSeparator': {
    display: 'none !important',
  },
  '& .MuiButtonBase-root': {
    backgroundColor: '#0A1A2C',
  },
  '& .MuiDataGrid-cell': {
    borderBottom: 'none',
  },
  '.css-ltf0zy-MuiDataGrid-iconButtonContainer': {
    visibility: 'hidden !important',
  },
  '.MuiDataGrid-columnHeader': {
    backgroundColor: '#0A1A2C',
    padding: '0px 6px !important',
  },
  '.MuiCheckbox-root': {
    backgroundColor: 'transparent !important',
  },
  '& .MuiDataGrid-cell, & .MuiDataGrid-columnHeaderTitle': {
    textOverflow: 'ellipsis',
  },
};

const BASE_SLOT_PROPS = {
  panel: {
    sx: {
      top: '-120px !important',
    },
  },
  toolbar: {
    sx: {
      marginBottom: '20px !important',
    },
  },
};

// ─────────────────────────────────────────────────────────────────────────────
// Slot components — defined OUTSIDE so MUI never sees a new component type
// on re-render (which would cause full unmount/remount of the slot).
// Data is passed via slotProps, not closures.
// ─────────────────────────────────────────────────────────────────────────────

const FooterSlot = ({
  footer,
  summary,
  rows,
  columns,
  pagination,
}: {
  footer?: React.ReactNode;
  summary?: any;
  rows: any[];
  columns: any[];
  pagination?: boolean;
}) => (
  <div className="d-flex flex-1 flex-column">
    {footer && footer}
    {summary && <CustomSummary rows={rows} columns={columns} summary={summary} />}
    {pagination && <GridPagination />}
  </div>
);

const ToolbarSlot = ({
  canExport,
  canPrint,
  search,
  headerProps,
  isHeader,
  printOptions,
  onPrint,
  onExportCsv,
}: {
  canExport?: any;
  canPrint?: any;
  search?: any;
  headerProps?: any;
  isHeader?: boolean;
  printOptions?: any;
  onPrint?: () => void;
  onExportCsv?: () => void; // ← add
}) => (
  <CustomGridToolbar
    canExport={canExport}
    canPrint={canPrint}
    search={search}
    headerProps={headerProps}
    isHeader={isHeader}
    printOptions={printOptions}
    onPrint={onPrint}
    onExportCsv={onExportCsv}
  />
);

const NoRowsSlot = ({ message }: { message?: string }) => (
  <CustomDataGridNoRowOverlay message={message || 'No Rows'} />
);

const LoadingSlot = () => <GridLoadingOverlay style={{ backgroundColor: 'rgb(0 0 0 / 29%)' }} />;

// Stable slots object — never recreated because components are defined outside
const SLOTS = {
  footer: FooterSlot,
  toolbar: ToolbarSlot,
  noRowsOverlay: NoRowsSlot,
  loadingOverlay: LoadingSlot,
} as const;

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

interface CustomDatagridProps {
  rows: any[];
  columns: any[];
  rowHeight?: number | 'auto';
  pagination?: boolean;
  summary?: any;
  hiddenCols?: any;
  toolbar?: any;
  loading?: boolean;
  rowSelection?: boolean;
  onEditCellChange?: (params: any) => void;
  pageNumber?: 5 | 10 | 15 | 50 | 100;
  sorting?: GridSortModel;
  canExport?: any;
  canPrint?: any;
  noRowsMessage?: string;
  search?: any;
  footer?: React.ReactNode;
  cellBorder?: boolean;
  isZeroMargin?: boolean;
  headerProps?: any;
  isHeader?: boolean;
  onRowClick?: (params: any) => void;
  apiRef?: any;
  autoHeight?: boolean;
  rowSelectionModel?: any[];
  onRowSelectionModelChange?: (params: any) => void;
  checkboxSelection?: boolean;
  isRowSelectable?: (params: any) => boolean;
  columnVisibilityModel?: any;
  onRowDoubleClick?: (params: any) => void;
  height?: number;
  sortingOrder?: any[];
  onSortModelChange?: (params: any) => void;
  printOptions?: any;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const printGridRows = (rows: any[], columns: any[]) => {
  const printableColumns = columns.filter(
    (col) =>
      col.field !== '__check__' &&
      col.field?.toLowerCase() !== 'action' &&
      col.headerName?.toLowerCase() !== 'action' &&
      col.headerName?.toLowerCase() !== 'actions' &&
      col.field?.toLowerCase() !== 'actions' &&
      col.field?.toLowerCase() !== 'avatar' &&
      col.type !== 'actions' &&
      col.headerName &&
      col.headerName.trim() !== '',
  );

  const tableRows = rows
    .map(
      (row) =>
        `<tr>${printableColumns
          .map((col) => {
            let val = col.valueGetter
              ? col.valueGetter({ row, field: col.field, value: row[col.field] })
              : row[col.field];

            if (col.valueFormatter) {
              val = col.valueFormatter({ value: val, field: col.field, row });
            }

            if (val === undefined || val === null) val = '';
            if (typeof val === 'object') val = JSON.stringify(val);

            return `<td style="border:1px solid #ccc;padding:6px 10px;font-size:12px;">${val}</td>`;
          })
          .join('')}</tr>`,
    )
    .join('');

  const html = `
    <html>
      <head>
        <title>Print</title>
        <style>
          @page { size: landscape; margin: 15mm; }
          body { font-family: Arial, sans-serif; margin: 20px; }
          table { border-collapse: collapse; width: 100%; }
          th {
            background: #0A1A2C;
            color: white;
            border: 1px solid #ccc;
            padding: 8px 10px;
            font-size: 12px;
            text-align: left;
          }
          td { font-size: 12px; }
          tr:nth-child(even) { background: #f5f5f5; }
          @media print { body { margin: 0; } }
        </style>
      </head>
      <body>
        <table>
          <thead>
            <tr>${printableColumns.map((col) => `<th>${col.headerName}</th>`).join('')}</tr>
          </thead>
          <tbody>${tableRows}</tbody>
        </table>
      </body>
    </html>
  `;

  // ── Hidden iframe approach — no new tab ──────────────────────────────────
  const iframe = document.createElement('iframe');
  iframe.style.cssText = 'position:fixed;top:-9999px;left:-9999px;width:0;height:0;border:none;';
  document.body.appendChild(iframe);

  const doc = iframe.contentWindow?.document;
  if (!doc) return;

  doc.open();
  doc.write(html);
  doc.close();

  // Wait for content to load before printing
  iframe.onload = () => {
    iframe.contentWindow?.focus();
    iframe.contentWindow?.print();
    // Remove iframe after print dialog closes
    setTimeout(() => document.body.removeChild(iframe), 1000);
  };
};

const exportGridRowsToCsv = (rows: any[], columns: any[]) => {
  const printableColumns = columns.filter(
    (col) =>
      col.field !== '__check__' &&
      col.field?.toLowerCase() !== 'action' &&
      col.headerName?.toLowerCase() !== 'action' &&
      col.headerName?.toLowerCase() !== 'actions' &&
      col.field?.toLowerCase() !== 'actions' &&
      col.field?.toLowerCase() !== 'avatar' &&
      col.type !== 'actions' &&
      col.headerName &&
      col.headerName.trim() !== '',
  );

  const escape = (val: any): string => {
    if (val === undefined || val === null) return '';
    const str = typeof val === 'object' ? JSON.stringify(val) : String(val);
    // Wrap in quotes if contains comma, quote, or newline
    return str.includes(',') || str.includes('"') || str.includes('\n')
      ? `"${str.replace(/"/g, '""')}"`
      : str;
  };

  const header = printableColumns.map((col) => escape(col.headerName)).join(',');

  const rowLines = rows.map((row) =>
    printableColumns
      .map((col) => {
        let val = col.valueGetter
          ? col.valueGetter({ row, field: col.field, value: row[col.field] })
          : row[col.field];

        if (col.valueFormatter) {
          val = col.valueFormatter({ value: val, field: col.field, row });
        }

        return escape(val);
      })
      .join(','),
  );

  const csv = [header, ...rowLines].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);

  const link = document.createElement('a');
  link.href = url;
  link.download = `export_${new Date().toISOString().slice(0, 10)}.csv`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const CustomDatagrid: React.FC<CustomDatagridProps> = React.memo(
  ({
    rows,
    columns,
    rowHeight,
    pagination,
    summary,
    hiddenCols,
    loading,
    rowSelection,
    onEditCellChange,
    pageNumber,
    sorting,
    canExport,
    canPrint,
    noRowsMessage,
    search,
    footer,
    cellBorder,
    isZeroMargin,
    headerProps,
    isHeader,
    onRowClick,
    apiRef,
    checkboxSelection = false,
    onRowSelectionModelChange,
    isRowSelectable,
    columnVisibilityModel,
    rowSelectionModel,
    onRowDoubleClick,
    height = 23,
    sortingOrder,
    onSortModelChange,
  }: CustomDatagridProps) => {
    // Add this helper outside the component
    const internalApiRef = useGridApiRef();
    const effectiveApiRef = apiRef || internalApiRef;
    const [hasVisibleRows, setHasVisibleRows] = useState(true);

    // ── Memoized rows — prevents new array reference on every render ──────────
    const mappedRows = useMemo(
      () => rows.map((item, index) => ({ id: index + 1, ...item })),
      [rows],
    );

    // ── Memoized callbacks ────────────────────────────────────────────────────
    const getRowHeight = useCallback((): number | 'auto' => rowHeight || 'auto', [rowHeight]);

    const getRowClassName = useCallback((params: any): string => {
      const row = params.row;
      const classes = [
        row.isFullRowNotification ? 'notification-full-row' : '',
        params.indexRelativeToCurrentPage % 2 === 0 ? 'rdg-row-even' : 'rdg-row-odd',
        Number.isInteger(row.status) && [4, 6, 31].includes(row.status) ? 'deleted-row-red' : '',
        row.group ? 'row-roles-group' : 've',
        row.inventoryOf === 2 ? 'vehicle-da-inventory' : '',
        row.statusClass || '',
        row.disable ? 'row-disable' : '',
        row.duplicate ? 'row-duplicate' : '',
        row.failed ? 'row-failed' : '',
      ];
      return classes.filter(Boolean).join(' ');
    }, []); // row data comes through params — no external deps needed

    const handleFilterModelChange = useCallback(() => {
      setTimeout(() => {
        const count =
          gridPaginatedVisibleSortedGridRowEntriesSelector(effectiveApiRef)?.length ?? rows.length;
        setHasVisibleRows(count > 0);
      }, 0);
    }, [effectiveApiRef, rows.length]);

    // ── Memoized sx — recomputed only when relevant props change ─────────────
    const sx = useMemo<SxProps<Theme>>(
      () => ({
        ...BASE_SX,
        '&.MuiDataGrid-root--densityCompact .MuiDataGrid-cell, &.MuiDataGrid-root--densityStandard .MuiDataGrid-cell, &.MuiDataGrid-root--densityComfortable .MuiDataGrid-cell':
          {
            py: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
            px: isZeroMargin ? '0' : rowHeight === 'auto' ? '15px' : '0.4rem',
            borderRadius: isZeroMargin ? '0.3rem' : '0',
          },
        '& .MuiDataGrid-cell': {
          borderBottom: 'none',
          ...(cellBorder && {
            border: 2,
            borderColor: '#495d73',
            borderRight: 0,
            borderTop: 0,
          }),
        },
        '& .MuiDataGrid-virtualScroller': {
          minHeight: `${rows?.length === 0 || !hasVisibleRows ? '200px' : 'auto'} !important`,
          maxHeight: `calc(100vh - ${height}rem)`,
          overflowY: 'auto',
        },
      }),
      [isZeroMargin, rowHeight, cellBorder, rows?.length, hasVisibleRows, height],
    );

    // ── Memoized slotProps — pass dynamic data here instead of via closures ──
    // Inside the component, create a stable callback
    const handlePrint = useCallback(() => {
      printGridRows(rows, columns);
    }, [rows, columns]);

    const handleExportCsv = useCallback(() => {
      exportGridRowsToCsv(rows, columns);
    }, [rows, columns]);

    const slotProps = useMemo(
      () => ({
        ...BASE_SLOT_PROPS,
        footer: { footer, summary, rows, columns, pagination },
        toolbar: {
          canExport,
          canPrint,
          search,
          headerProps,
          isHeader,
          onPrint: handlePrint,
          onExportCsv: handleExportCsv, // ← add
        },
        noRowsOverlay: { message: noRowsMessage },
      }),
      [
        footer,
        summary,
        rows,
        columns,
        pagination,
        canExport,
        canPrint,
        search,
        headerProps,
        isHeader,
        noRowsMessage,
        handlePrint,
        handleExportCsv, // ← add
      ],
    );

    // ── Memoized initialState — only computed once (sorting/hiddenCols are
    //    initial values so they don't need to trigger re-renders) ─────────────
    const initialState = useMemo(
      () => ({
        pagination: {
          paginationModel: { pageSize: pageNumber ?? 100 },
        },
        columns: hiddenCols,
        sorting: { sortModel: sorting },
      }),
      [], // intentionally empty — these are truly initial-only values
    );

    return (
      <div style={{ width: '100%' }}>
        <DataGrid
          style={{ color: 'white', borderStyle: 'none' }}
          rows={mappedRows}
          columns={columns}
          getRowHeight={getRowHeight}
          getRowClassName={getRowClassName}
          processRowUpdate={onEditCellChange}
          checkboxSelection={checkboxSelection}
          disableColumnMenu={true}
          disableRowSelectionOnClick={false}
          onFilterModelChange={handleFilterModelChange}
          apiRef={effectiveApiRef}
          rowSelection={rowSelection}
          onRowClick={onRowClick}
          onRowDoubleClick={onRowDoubleClick}
          getRowId={(row) => row.id}
          pageSizeOptions={[5, 10, 15, 50, 100]}
          loading={loading}
          onSortModelChange={onSortModelChange}
          sortingOrder={sortingOrder || ['asc', 'desc']}
          isRowSelectable={isRowSelectable}
          columnVisibilityModel={columnVisibilityModel}
          rowSelectionModel={rowSelectionModel}
          onRowSelectionModelChange={onRowSelectionModelChange}
          initialState={initialState}
          slots={SLOTS}
          slotProps={slotProps as any}
          sx={sx}
          disableVirtualization
        />
      </div>
    );
  },
);

CustomDatagrid.displayName = 'CustomDatagrid';

export default CustomDatagrid;
